import Container from '../components/Container';
import StatusBadge from '../components/StatusBadge';
import {
  Search,
  Bell,
  Lock as LockIcon,
  ChevronDown,
  Download,
  Plus,
  ArrowRight,
} from 'lucide-react';

type RowState = 'active' | 'rate-limited' | 'skeleton' | 'pending';

type Row = {
  initial: string;
  avatarBg: string;
  name: string;
  email: string;
  provider: string;
  mfa: { label: string; ok: boolean } | null;
  lastSignIn: string;
  id: string;
  state: RowState;
};

const rows: Row[] = [
  {
    initial: 'K',
    avatarBg: 'bg-violet',
    name: 'Kira Park',
    email: 'kira@stellar.dev',
    provider: 'GitHub',
    mfa: { label: '✓ TOTP', ok: true },
    lastSignIn: '2m ago',
    id: 'usr_01HJX3K2',
    state: 'active',
  },
  {
    initial: 'D',
    avatarBg: 'bg-cyan',
    name: 'Daichi Aoki',
    email: 'daichi@aurora.io',
    provider: 'Google',
    mfa: { label: '✓ TOTP', ok: true },
    lastSignIn: '14m ago',
    id: 'usr_01HJX2YA',
    state: 'active',
  },
  {
    initial: 'M',
    avatarBg: 'bg-amber',
    name: 'Mira Vasquez',
    email: 'mira.v@cobalt.run',
    provider: 'Microsoft',
    mfa: { label: '✓ Email', ok: true },
    lastSignIn: '1h ago',
    id: 'usr_01HJX1WD',
    state: 'active',
  },
  {
    initial: 'J',
    avatarBg: 'bg-pink',
    name: 'Jules Okonkwo',
    email: 'j.okonkwo@iridium.dev',
    provider: 'GitHub',
    mfa: null,
    lastSignIn: '3h ago',
    id: 'usr_01HJWZ8B',
    state: 'rate-limited',
  },
  {
    initial: 'A',
    avatarBg: 'bg-sky-500',
    name: 'Anya Chen',
    email: 'anya@lightyear.app',
    provider: 'Magic link',
    mfa: { label: '✓ TOTP', ok: true },
    lastSignIn: '5h ago',
    id: 'usr_01HJWY5R',
    state: 'active',
  },
  {
    initial: '',
    avatarBg: '',
    name: '',
    email: '',
    provider: '',
    mfa: null,
    lastSignIn: '',
    id: '',
    state: 'skeleton',
  },
  {
    initial: 'S',
    avatarBg: 'bg-slate100',
    name: 'Sasha Klein',
    email: 'sasha@orbital.so',
    provider: '—',
    mfa: null,
    lastSignIn: 'Never',
    id: 'usr_01HJWX1Z',
    state: 'pending',
  },
];

export default function Dashboard() {
  return (
    <main className="bg-slate50">
      <Container className="py-12">
        {/* preview note */}
        <p className="mb-4 font-mono text-[11px] text-muted">
          03 · Dashboard preview · neutral slate + violet only
        </p>

        {/* Browser chrome */}
        <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-elev">
          {/* title bar */}
          <div className="flex items-center justify-between border-b border-border bg-slate50 px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="size-3 rounded-full bg-[#FF5F57]" />
              <span className="size-3 rounded-full bg-[#FFBD2E]" />
              <span className="size-3 rounded-full bg-[#28C940]" />
              <div className="ml-4 flex items-center gap-1 text-muted">
                <span>‹</span>
                <span className="text-borderStrong">›</span>
              </div>
            </div>
            <div className="flex flex-1 items-center gap-2 px-6">
              <div className="flex w-full max-w-[640px] items-center gap-2 rounded-md border border-border bg-surface px-3 py-1.5">
                <LockIcon className="size-3 text-ok" />
                <span className="font-mono text-[12px] text-ink">app.pryma.dev</span>
                <span className="font-mono text-[12px] text-muted">
                  /projects/proj_a5n/end-users
                </span>
              </div>
            </div>
            <span className="text-muted">⋮</span>
          </div>

          {/* App layout: sidebar + main */}
          <div className="grid grid-cols-12">
            {/* SIDEBAR */}
            <aside className="col-span-3 border-r border-border bg-slate50 p-4 lg:col-span-2">
              {/* logo */}
              <div className="mb-5 flex items-center gap-2 px-1">
                <svg width="18" height="18" viewBox="0 0 32 32" aria-hidden>
                  <path d="M11 4 L21 26 L1 26 Z" fill="#7C3AED" />
                  <path d="M14 8 L22 26 L6 26 Z" fill="#06B6D4" />
                </svg>
                <span className="text-[16px] font-bold text-ink">pryma.</span>
              </div>

              {/* project switcher */}
              <button className="flex w-full items-center justify-between rounded-lg border border-border bg-surface px-2.5 py-2 text-left transition-colors hover:border-borderStrong">
                <div className="flex items-center gap-2">
                  <span className="grid size-5 place-items-center rounded bg-violet text-[10px] font-bold text-white">
                    S
                  </span>
                  <div className="min-w-0">
                    <p className="text-[10px] text-muted">Stellar</p>
                    <p className="truncate text-[12px] font-bold text-ink">Production</p>
                  </div>
                </div>
                <ChevronDown className="size-3.5 text-muted" />
              </button>

              {/* nav */}
              <p className="mt-6 px-1 text-[10px] font-bold uppercase tracking-wider text-muted">
                Workspace
              </p>
              <ul className="mt-2 space-y-0.5">
                {[
                  { label: 'End users', active: true, count: '24K' },
                  { label: 'Overview' },
                  { label: 'Auth methods' },
                  { label: 'Sessions' },
                  { label: 'MFA settings' },
                  { label: 'Webhooks' },
                  { label: 'Audit log', isNew: true },
                ].map(item => (
                  <li key={item.label}>
                    <a
                      href="#"
                      className={`relative flex items-center justify-between rounded-md px-2.5 py-1.5 text-[13px] transition-colors ${
                        item.active
                          ? 'bg-violetSoft text-violet font-semibold'
                          : 'text-body hover:bg-slate100 hover:text-ink'
                      }`}
                    >
                      {item.active && (
                        <span className="absolute left-0 top-1.5 h-5 w-[3px] rounded-full bg-violet" />
                      )}
                      <span>{item.label}</span>
                      {item.count && (
                        <span className="rounded-full border border-violetLine bg-surface px-1.5 py-px font-mono text-[10px] font-bold text-violet">
                          {item.count}
                        </span>
                      )}
                      {item.isNew && (
                        <span className="rounded-full border border-pink/40 bg-pinkSoft px-1.5 py-px text-[9px] font-bold text-pinkMid">
                          NEW
                        </span>
                      )}
                    </a>
                  </li>
                ))}
              </ul>

              <p className="mt-6 px-1 text-[10px] font-bold uppercase tracking-wider text-muted">
                Project
              </p>
              <ul className="mt-2 space-y-0.5">
                {['API keys', 'Domains', 'Members', 'Settings'].map(l => (
                  <li key={l}>
                    <a
                      href="#"
                      className="block rounded-md px-2.5 py-1.5 text-[13px] text-body transition-colors hover:bg-slate100 hover:text-ink"
                    >
                      {l}
                    </a>
                  </li>
                ))}
              </ul>

              {/* env toggle */}
              <div className="mt-8 grid grid-cols-2 gap-1 rounded-lg border border-border bg-surface p-1">
                <button className="rounded-md px-2 py-1.5 text-[12px] text-body hover:text-ink">
                  Dev
                </button>
                <button className="flex items-center justify-center gap-1.5 rounded-md bg-violet px-2 py-1.5 text-[12px] font-semibold text-white">
                  <span className="size-1 rounded-full bg-white" />
                  Prod
                </button>
              </div>
              <p className="mt-3 px-1 font-mono text-[11px] text-muted">ashod@stellar.dev</p>
            </aside>

            {/* MAIN */}
            <div className="col-span-9 lg:col-span-10">
              {/* TOP BAR */}
              <div className="flex h-14 items-center justify-between border-b border-border bg-surface px-5">
                <div className="flex items-center gap-2 text-[13px]">
                  <span className="text-muted">Production</span>
                  <span className="text-borderStrong">/</span>
                  <span className="font-bold text-ink">End users</span>
                </div>
                <div className="hidden flex-1 max-w-[420px] mx-8 items-center gap-2 rounded-lg border border-border bg-slate50 px-3 py-1.5 md:flex">
                  <Search className="size-3.5 text-muted" />
                  <span className="flex-1 text-[13px] text-muted">
                    Search end users, sessions, audit logs…
                  </span>
                  <kbd className="rounded border border-border bg-surface px-1.5 font-mono text-[11px] font-semibold text-body">
                    ⌘K
                  </kbd>
                </div>
                <div className="flex items-center gap-3">
                  <a
                    href="#"
                    className="hidden items-center gap-1.5 rounded-full border border-pink/40 bg-pinkSoft px-2.5 py-1 text-[12px] font-bold text-pinkMid hover:bg-pink/10 sm:inline-flex"
                  >
                    <span className="size-1.5 rounded-full bg-pink" />
                    What's new <ArrowRight className="size-3" />
                  </a>
                  <button className="relative grid size-8 place-items-center text-body hover:text-ink">
                    <Bell className="size-4" />
                    <span className="absolute right-1 top-1 size-1.5 rounded-full bg-err" />
                  </button>
                  <span className="grid size-7 place-items-center rounded-full bg-violet text-[11px] font-bold text-white">
                    AA
                  </span>
                </div>
              </div>

              {/* PAGE BODY */}
              <div className="p-6">
                {/* page header */}
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <h1 className="text-[24px] font-bold text-ink">End users</h1>
                    <p className="mt-1 font-mono text-[12px] text-body">
                      24,128 total · 312 added this week · last sync 2m ago
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-3 py-1.5 text-[13px] text-body hover:border-borderStrong hover:text-ink">
                      <Download className="size-3.5" /> Export
                    </button>
                    <button className="inline-flex items-center gap-1.5 rounded-md bg-pryma-grad px-3 py-1.5 text-[13px] font-semibold text-white">
                      <Plus className="size-3.5" /> Invite end user
                    </button>
                  </div>
                </div>

                {/* KPI cards */}
                <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
                  {[
                    {
                      label: 'TOTAL END USERS',
                      val: '24,128',
                      delta: '▲ +312',
                      deltaCls: 'text-ok',
                      period: 'vs. last 7 days',
                    },
                    {
                      label: 'SIGN-INS (24H)',
                      val: '184,592',
                      delta: '▲ +12.4%',
                      deltaCls: 'text-ok',
                      period: 'vs. yesterday',
                    },
                    {
                      label: 'ACTIVE SESSIONS',
                      val: '9,841',
                      delta: '▼ −0.8%',
                      deltaCls: 'text-muted',
                      period: 'vs. last hour',
                    },
                    {
                      label: 'MFA ADOPTION',
                      val: '73.2%',
                      delta: '▲ +2.1pt',
                      deltaCls: 'text-ok',
                      period: 'vs. last 30 days',
                    },
                  ].map(k => (
                    <div
                      key={k.label}
                      className="rounded-xl border border-border bg-surface p-4 shadow-hair"
                    >
                      <p className="text-[10px] font-bold tracking-wider text-muted">{k.label}</p>
                      <div className="mt-2 flex items-baseline justify-between gap-2">
                        <p className="num-tab text-[28px] font-bold tracking-tighter text-ink">
                          {k.val}
                        </p>
                        <span className={`num-tab font-mono text-[12px] font-bold ${k.deltaCls}`}>
                          {k.delta}
                        </span>
                      </div>
                      <p className="mt-1 text-[11px] text-muted">{k.period}</p>
                    </div>
                  ))}
                </div>

                {/* TABLE */}
                <div className="mt-6 overflow-hidden rounded-xl border border-border bg-surface">
                  <div className="grid grid-cols-12 gap-2 border-b border-border bg-slate50 px-5 py-2.5 text-[10px] font-bold uppercase tracking-wider text-muted">
                    <span className="col-span-4">User</span>
                    <span className="col-span-2">Provider</span>
                    <span className="col-span-1">MFA</span>
                    <span className="col-span-2">Last sign-in</span>
                    <span className="col-span-2">ID</span>
                    <span className="col-span-1 text-right">Status</span>
                  </div>
                  <ul>
                    {rows.map((r, i) => {
                      if (r.state === 'skeleton') {
                        return (
                          <li
                            key={`skel-${i}`}
                            className="grid grid-cols-12 items-center gap-2 border-b border-slate100 px-5 py-3 last:border-b-0"
                          >
                            <div className="col-span-4 flex items-center gap-3">
                              <span className="size-7 rounded-full bg-slate100" />
                              <div className="flex-1 space-y-1.5">
                                <span className="block h-2.5 w-24 rounded bg-slate100" />
                                <span className="block h-2 w-36 rounded bg-slate100" />
                              </div>
                            </div>
                            <span className="col-span-2 h-3 w-16 rounded bg-slate100" />
                            <span className="col-span-1 h-3 w-10 rounded bg-slate100" />
                            <span className="col-span-2 h-3 w-14 rounded bg-slate100" />
                            <span className="col-span-2 h-3 w-20 rounded bg-slate100" />
                            <span className="col-span-1 ml-auto h-4 w-14 rounded-full bg-slate100" />
                          </li>
                        );
                      }
                      const isWarn = r.state === 'rate-limited';
                      return (
                        <li
                          key={r.id || i}
                          className={`relative grid grid-cols-12 items-center gap-2 border-b border-slate100 px-5 py-3 last:border-b-0 ${
                            isWarn ? 'bg-warnSoft/60' : ''
                          }`}
                        >
                          {isWarn && <span className="absolute inset-y-0 left-0 w-[3px] bg-warn" />}
                          <div className="col-span-4 flex items-center gap-3">
                            <span
                              className={`grid size-7 place-items-center rounded-full text-[11px] font-bold text-white ${r.avatarBg}`}
                            >
                              {r.initial}
                            </span>
                            <div>
                              <p className="text-[13px] font-bold text-ink">{r.name}</p>
                              <p className="font-mono text-[11px] text-muted">{r.email}</p>
                            </div>
                          </div>
                          <span className="col-span-2 text-[13px] text-body">{r.provider}</span>
                          <span
                            className={`col-span-1 text-[12px] font-bold ${r.mfa?.ok ? 'text-ok' : 'text-muted'}`}
                          >
                            {r.mfa ? r.mfa.label : '—'}
                          </span>
                          <span className="num-tab col-span-2 font-mono text-[12px] text-ink">
                            {r.lastSignIn}
                          </span>
                          <span className="col-span-2 font-mono text-[11px] text-muted">
                            {r.id}
                          </span>
                          <span className="col-span-1 ml-auto">
                            <StatusBadge
                              status={
                                r.state === 'active'
                                  ? 'active'
                                  : r.state === 'rate-limited'
                                    ? 'rate-limited'
                                    : 'pending'
                              }
                            />
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {/* pagination */}
                  <div className="flex items-center justify-between border-t border-border bg-slate50 px-5 py-2.5">
                    <p className="text-[12px] text-body">Showing 1–7 of 24,128</p>
                    <div className="flex items-center gap-3 text-[12px]">
                      <button className="text-muted hover:text-body">‹ Prev</button>
                      <span className="font-mono text-body">1 / 3,447</span>
                      <button className="font-semibold text-violet hover:text-violetMid">
                        Next ›
                      </button>
                    </div>
                  </div>
                </div>

                {/* ACTIVITY FEED */}
                <div className="mt-6 overflow-hidden rounded-xl border border-border bg-surface">
                  <div className="flex items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="size-1.5 animate-pulse rounded-full bg-ok" />
                      <span className="text-[14px] font-bold text-ink">Recent activity</span>
                      <span className="font-mono text-[11px] text-muted">
                        · live · pryma.realtime
                      </span>
                    </div>
                    <a
                      href="#"
                      className="text-[12px] font-semibold text-violet hover:text-violetMid"
                    >
                      Open audit log →
                    </a>
                  </div>
                  <ul className="border-t border-slate100">
                    {[
                      {
                        ts: '13:42:18',
                        dot: 'bg-ok',
                        evt: 'auth.sign_in.success',
                        who: 'kira@stellar.dev',
                        meta: 'github_oauth',
                        via: 'via api.pryma.dev',
                      },
                      {
                        ts: '13:41:56',
                        dot: 'bg-ok',
                        evt: 'auth.mfa.challenge_passed',
                        who: 'daichi@aurora.io',
                        meta: 'totp',
                        via: 'via api.pryma.dev',
                      },
                      {
                        ts: '13:39:02',
                        dot: 'bg-violet',
                        evt: 'auth.session.refreshed',
                        who: '9 sessions',
                        meta: 'auto',
                        via: 'via cron worker',
                      },
                      {
                        ts: '13:38:11',
                        dot: 'bg-warn',
                        evt: 'auth.rate_limit.triggered',
                        who: 'j.okonkwo@iridium.dev',
                        meta: '5min cooldown',
                        via: 'via api.pryma.dev',
                      },
                    ].map(e => (
                      <li
                        key={e.ts + e.evt}
                        className="grid grid-cols-12 items-center gap-2 border-b border-slate100 px-5 py-2 last:border-b-0 font-mono text-[12px]"
                      >
                        <span className="col-span-2 text-muted">{e.ts}</span>
                        <span className="col-span-4 flex items-center gap-2 font-semibold text-ink">
                          <span className={`size-1.5 rounded-full ${e.dot}`} />
                          {e.evt}
                        </span>
                        <span className="col-span-3 text-body">{e.who}</span>
                        <span className="col-span-1 text-body">{e.meta}</span>
                        <span className="col-span-2 text-right text-muted">{e.via}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Container>
    </main>
  );
}
